import * as React from "react";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { AppBar, Toolbar, Link, Breadcrumbs } from "@mui/material";
import HomeIcon from "../../assets/icons/home.svg";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";
import { fetchScenario } from "../helpers/FetchScenario";
import { fetchSimulationById } from "../helpers/FetchSimulationById";
import DefaultButton from "../simple/DefaultButton";

import { useAuth } from "../../contexts/AuthContext";

export function TopNav({ topnavminimal, isMobile }) {
  const theme = useTheme();
  const { brand, trainingProgram } = React.useContext(BrandContext);

  const { authToken } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();
  const [scenarioData, setScenarioData] = React.useState(null);
  const [scenarioName, setScenarioName] = React.useState("Scenario");

  const fullPath = location.pathname.split("/dashboard/")[1] || "";
  const pathSegments = fullPath.split("/").filter(Boolean);
  let breadcrumbs = [];

  const handleBack = (e) => {
    e.preventDefault();
    // window.scrollTo(0,0); no worky
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/"); // fallback route (e.g., home)
    }
  };

  const buildBreadCrumbs = () => {
    const lastSegment = pathSegments[pathSegments.length - 1];
    const leafNodeRedirect = `/dashboard/${pathSegments.join("/")}`;
    if (!lastSegment || lastSegment === "home") {
      return [
        {
          name: "Home",
          to: "/dashboard/home",
        },
      ];
    }
    if (lastSegment === "simulations") {
      return [
        {
          name: "Performance Spotlight",
          to: leafNodeRedirect,
        },
      ];
    }
    if (lastSegment === "team") {
      return [
        {
          name: "My Team",
          to: leafNodeRedirect,
        },
      ];
    }
    if (
      ["trainingprogram", "simulate", "feedback"].some((word) =>
        pathSegments.includes(word)
      )
    ) {
      breadcrumbs.push(
        {
          name: "My programs",
          to: `/dashboard/trainingprogram/${
            trainingProgram ? trainingProgram.id : ""
          }`,
        },
        {
          name: trainingProgram ? trainingProgram.name : "Training Program",
          to: `/dashboard/trainingprogram/${
            trainingProgram ? trainingProgram.id : ""
          }`,
        }
      );
    }
    if (["simulate", "feedback"].some((word) => pathSegments.includes(word))) {
      breadcrumbs.push({
        name: scenarioName,
        to: `/dashboard/simulate/${scenarioData ? scenarioData.id : ""}`,
      });
    }
    if (pathSegments.includes("feedback")) {
      // TODO: change to use dynamic frontend optimisations if possible
      fetchSimulationById(brand.api_base_url, authToken, lastSegment).then(
        (simulation) => {
          setScenarioName(simulation?.scenario?.name);
        }
      );
      breadcrumbs.push({
        name: "Results",
        to: leafNodeRedirect,
      });
    }
    return breadcrumbs;
  };

  useEffect(() => {
    if (scenarioData) {
      setScenarioName(scenarioData.name);
    }
  }, [scenarioData]);

  useEffect(() => {
    if (pathSegments.length !== 2 || pathSegments[0] !== "simulate") {
      return;
    }
    const loadScenario = async () => {
      const data = await fetchScenario(
        brand,
        authToken,
        pathSegments[pathSegments.length - 1]
      );
      if (data) {
        setScenarioData(data);
      }
    };
    loadScenario();
  }, [location.pathname, authToken, brand.api_base_url]);

  return (
    <AppBar
      component="div"
      position="relative"
      elevation={0}
      sx={{
        marginTop: {
          md: "0",
          sm: "15px",
          xs: "15px",
        },
        padding: { md: "0.75rem 2rem", sm: "0 1rem", xs: "0 1rem" },
        backgroundColor: theme.palette.background.primary,
        borderBottom: {
          md: "rgba(255,255,255,.1) 1px solid",
          sm: 0,
          xs: "0",
        },
        "& .mobile-breadcrumbs": {
          marginBottom: "20px",
          "& .mobile-back-link": {
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#D1C7FF",
            fontSize: "14px",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "none",
            },
          },
        },
      }}
    >
      {isMobile ? (
        <div className="mobile-breadcrumbs">
          {window.history.length > 1 &&
            !window.location.pathname.includes("home") &&
            window.location.pathname !== "/" && (
              <a
                href="#"
                onClick={(e) => handleBack(e)}
                className="mobile-back-link"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.8333 7.00008H1.16663M1.16663 7.00008L6.99996 12.8334M1.16663 7.00008L6.99996 1.16675"
                    stroke="#D1C7FF"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Back
              </a>
            )}
        </div>
      ) : (
        <Toolbar
          className="toolbar-container"
          sx={{
            width: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                sx={{
                  fontSize: "14px",
                  opacity: ".6",
                  position: "relative",
                  top: "1px",
                }}
              />
            }
            aria-label="breadcrumb"
            sx={{
              color: "white",
              display: "flex",
              alignItems: "center",
              padding: 0,
              margin: 0,
              minHeight: 0,
            }}
          >
            <Link
              component={RouterLink}
              to="/dashboard/home"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                marginRight: theme.customSpacing["xs"],
                "& img": {
                  width: "13px",
                  height: "13px",
                  opacity: ".6",
                  position: "relative",
                  top: "1px",
                },
              }}
            >
              <img src={HomeIcon} alt="Home" />
            </Link>
            {buildBreadCrumbs().map((crumb, i, { length }) => {
              return i + 1 === length ? (
                // <DefaultButton
                //   onClick={() => navigate(crumb.to)}
                //   padding={`${theme.customSpacing.md} ${theme.customSpacing.s}`}
                //   disableRipple={true}
                //   disableHover={true}
                // >
                //   {crumb.name}
                // </DefaultButton>
                <Link
                  key={crumb.to}
                  component={RouterLink}
                  to={crumb.to}
                  sx={{
                    color: "white",
                    textDecoration: "none",
                    fontSize: "12px",
                  }}
                >
                  {crumb.name}
                </Link>
              ) : (
                <Link
                  key={crumb.to}
                  component={RouterLink}
                  to={crumb.to}
                  sx={{
                    color: "white",
                    textDecoration: "none",
                    fontSize: "12px",
                    opacity: ".6",
                  }}
                >
                  {crumb.name}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Toolbar>
      )}
    </AppBar>
  );
}
